import { useMemo } from 'react';
import { ProductCardProps } from '../types';
import { getTargetProductDiscountInfo } from '../helper/product-target-coupon';
import { TargetCouponDiscount, TargetCouponSubtraction } from '@/types/coupon';
import { usePersonalizedCoupon } from '@/store/personalized-coupon';

export interface UseGetTargetProductCouponInfoReturnType {
  show: boolean;
  claimed: boolean;
  personalizedDiscount: TargetCouponDiscount | null;
  subtraction: TargetCouponSubtraction | null;
}

/**
 * 这个hook主要是获取当前商品是否展示定向券，以及定向券的领取状态和折扣信息
 * 统一逻辑，在ProductCard的多个组件中使用，避免重复逻辑
 * @param props ProductCardProps
 * @returns {
 *  show: boolean;
 *  claimed: boolean;
 *  personalizedDiscount: TargetCouponDiscount | null;
 *  subtraction: TargetCouponSubtraction | null;
 * }
 */

const useGetTargetProductCouponInfo = (props: Partial<ProductCardProps>): UseGetTargetProductCouponInfoReturnType => {
  const { data, productCouponProps } = props;
  const { id: productId } = data || {};
  const { show = true } = productCouponProps || {};

  const { personalizedCouponData } = usePersonalizedCoupon();

  const personalizedDiscount: TargetCouponDiscount | null = useMemo(() => {
    return getTargetProductDiscountInfo(personalizedCouponData?.discounts, productId);
  }, [personalizedCouponData?.discounts, productId]);

  const { claimed = false, subtraction = null } = personalizedDiscount || {};

  const isShowTargetCoupon = useMemo(() => {
    if (!show) return false;
    if (!personalizedDiscount) return false;
    const baseNumber = data?.base_price != null && data?.base_price !== '' ? Number(data?.base_price) : NaN;
    const priceNumber = data?.price != null && data?.price !== '' ? Number(data?.price) : NaN;
    const hasOff = Number.isFinite(baseNumber) && Number.isFinite(priceNumber) && baseNumber > priceNumber;
    if (hasOff && !claimed) return false;
    return true;
  }, [show, personalizedDiscount, data?.base_price, data?.price, claimed]);

  return {
    show: isShowTargetCoupon,
    claimed,
    personalizedDiscount,
    subtraction
  };
};

export default useGetTargetProductCouponInfo;
